<template>
  <v-sheet flat border class="pa-4 col-sm-12">
    <p class="text-subtitle-1 pt-3 text-primary">
      <span class="font-weight-bold">Thermostat</span>
      - Input values at execution time
    </p>
    <v-row>
      <v-col cols="12">
        <v-select
          v-model="localInputData.ControlId"
          :items="inputList"
          :item-title="(item) => item.Name || ''"
          :item-value="(item) => item.Id"
          label="Control Name"
          variant="outlined"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CurrentDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Current Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.ReadingDateTime"
          :rules="[isDate]"
          variant="outlined"
          density="compact"
          label="Reading Date/Time"
          type="datetime-local"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.DelayInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Delay in Minutes"
        />
      </v-col>
    </v-row>
    <BusinessHoursInput
      v-model:start-time="localInputData.SiteStartTime"
      v-model:end-time="localInputData.SiteEndTime"
    />
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.HeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Heat Setting"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.MinHeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Min Heat Setting"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.MaxHeatSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Max Heat Setting"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.CoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Cool Setting"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.MinCoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Min Cool Setting"
        />
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="localInputData.MaxCoolSetting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Max Cool Setting"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.Co2Setting"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="CO2 Setting (max)"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.Co2Level"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="CO2 Level (current)"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.FanStatus"
          :rules="[(v) => /^(on|off)$/i.test(v)]"
          variant="outlined"
          density="compact"
          label="Fan On/Auto Status"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.FrontKeyPad"
          :rules="[(v) => /^(on|off)$/i.test(v)]"
          variant="outlined"
          density="compact"
          label="Font Key Pad On/Off Value"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3">
        <v-select
          :items="systemEnum"
          :item-title="(item) => item.text || ''"
          :item-value="(item) => item.id"
          v-model="localInputData.System"
          label="The Current System Value of the A/C Unit"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.SystemDurationInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="System Status Duration (minutes)"
        />
      </v-col>
      <v-col cols="3">
        <v-select
          :items="runStatuses"
          :item-title="(item) => item.text || ''"
          :item-value="(item) => item.id"
          v-model="localInputData.RunStatus"
          label="Run Status"
          variant="outlined"
          density="compact"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.RunStatusDurationInMinutes"
          :rules="[isNumberGreaterThanZero]"
          variant="outlined"
          density="compact"
          label="Run Status Duration In Minutes"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.Temperature"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Current Space Temperature"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.SupplyAirTemperature"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Supply Air Temperature"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="localInputData.ReturnAirTemperature"
          :rules="[isNumber]"
          variant="outlined"
          density="compact"
          label="Return Air Temperature"
        />
      </v-col>
    </v-row>
  </v-sheet>
</template>
<script>
import {
  isNumber,
  isNumberGreaterThanZero,
  hasValue,
  isDate,
} from '@/Core.Patterns/Factory/validationFactory.ts';
import BusinessHoursInput from '@/Core.Service.Domain/Strategies/Strategies/Components/BusinessHoursInput.vue';
import { withBaseInputDefaults } from '@/Core.Service.Domain/Strategies/Strategies/Utils/baseInputDefaults.ts';

export default {
  name: 'ThermostatsInput',

  components: { BusinessHoursInput },
  data() {
    return {
      // line un with the RunStatus enum
      runStatuses: [
        { id: 0, shortName: 'CoolStage1', text: 'Cool Stage 1' },
        { id: 1, shortName: 'CoolStage2', text: 'Cool Stage 2' },
        { id: 2, shortName: 'Fan', text: 'Fan' },
        { id: 3, shortName: 'Fan2', text: 'Fan2' },
        { id: 4, shortName: 'HeatStage1', text: 'Heat Stage 1' },
        { id: 5, shortName: 'HeatStage2', text: 'Heat Stage 2' },
        { id: 6, shortName: 'Off', text: 'Off' },
        { id: 7, shortName: 'On', text: 'On' },
      ],
      // line up with the system enum
      systemEnum: [
        { id: 0, text: 'Auto' },
        { id: 1, text: 'Off' },
        { id: 2, text: 'Cool' },
        { id: 3, text: 'Heat' },
      ],
      localInputData: withBaseInputDefaults(this.InputData),
    };
  },

  computed: {
    inputList() {
      // see if we have a list of datasources of this very object in datasources
      const inputs = this.DataSources.find(
        (ds) => ds.Key === 'Thermostats'
      )?.Value;
      if (inputs) return inputs;
      // if not, we'll create one based on localInputData.ControlId/Name/FriendlyName values
      return [
        {
          Id: this.localInputData.ControlId,
          DeviceName: this.localInputData.ControlName,
          FriendlyName: this.localInputData.ControlFriendlyName,
          Name: `${this.localInputData.ControlFriendlyName} ( ${this.localInputData.ControlName} )`,
        },
      ];
    },
  },

  props: {
    InputData: {
      type: Object,
      required: true,
    },
    DataSources: {
      type: Array,
      required: true,
    },
  },

  watch: {
    'localInputData.ControlId'(newControlId) {
      // this handles the case where a user changes an entry in the Controls v-select
      // since we only get the ControlId (v-model), we need to set the rest
      const selected = this.inputList.find((i) => i.Id == newControlId);
      if (selected) {
        this.localInputData.ControlName = selected.Name;
        this.localInputData.ControlFriendlyName = selected.FriendlyName;
      } else {
        this.localInputData.ControlName = null;
        this.localInputData.ControlFriendlyName = null;
      }
    },
    localInputData: {
      handler(newVal) {
        this.$emit('update:model', newVal);
      },
      deep: true,
    },
  },

  methods: {
    isNumber,
    isNumberGreaterThanZero,
    hasValue,
    isDate,
  },
};
</script>
